import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { ActionButton } from '@augmentt-dev/ui-components'
import { actions as GWS_ACTIONS } from '../../../engage/integrations/gws/actions'
import { GWS, MODAL_NAMES, OFFICE365 } from '../../../engage/utils'
import { ACTIONS } from '../../IntegrationModals'

const M365_BUTTONS = [
  {
    action: ACTIONS.RESET_PASSWORD,
    messageId: 'ENGAGE.RESET_PASSWORD',
    icon: 'fas fa-key',
    excludeWhen: ({ selectedEmployees }) => selectedEmployees.length > 1,
  },
  {
    action: ACTIONS.BLOCK_SIGNIN,
    messageId: 'ENGAGE.BLOCK_SIGNIN',
    icon: 'fas fa-ban',
    excludeWhen: ({ selectedEmployees }) => selectedEmployees.every((emp) => !emp.enabled),
  },
  {
    action: ACTIONS.UNBLOCK_SIGNIN,
    messageId: 'ENGAGE.UNBLOCK_SIGNIN',
    icon: 'far fa-circle-check',
    excludeWhen: ({ selectedEmployees }) => selectedEmployees.every((emp) => emp.enabled),
  },
  {
    action: ACTIONS.SIGNOUT_OF_ALL_APPS,
    messageId: 'ENGAGE.SIGN_OUT_ALL_APPS',
    icon: 'fas fa-right-from-bracket',
    excludeWhen: ({ selectedEmployees }) => selectedEmployees.length > 1,
  },
]

const GWS_BUTTONS = [
  {
    action: GWS_ACTIONS.GWS_DISABLE_USER,
    messageId: 'ENGAGE.SUSPEND_SIGN_IN',
    icon: 'fas fa-ban',
    excludeWhen: ({ selectedEmployees }) => selectedEmployees.every((emp) => !emp.enabled),
  },
  {
    action: GWS_ACTIONS.GWS_ENABLE_USER,
    messageId: 'ENGAGE.UNSUSPEND_SIGN_IN',
    icon: 'far fa-circle-check',
    excludeWhen: ({ selectedEmployees }) => selectedEmployees.every((emp) => emp.enabled),
  },
]

function EmployeeActionButton({ action, icon, messageId, disabled = false, onAction }) {
  const handleClick = useCallback(() => {
    onAction(action)
  }, [action, onAction])

  return (
    <ActionButton icon={icon} label={<FormattedMessage id={messageId} />} disabled={disabled} onClick={handleClick} />
  )
}

EmployeeActionButton.propTypes = {
  action: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  messageId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onAction: PropTypes.func.isRequired,
}

function EmployeeActionsSection({
  isAuthorized,
  openIntegrationModal,
  selectedEmployees,
  overrideEngageLicensing = false,
  integrationType = OFFICE365,
  setAction,
  setModal,
}) {
  const isGws = integrationType === GWS
  const isAuthorizedForEngage = selectedEmployees.every((emp) => emp.isAuthorizedForEngage)
  const areActionsEnabled = isAuthorized && (isAuthorizedForEngage || overrideEngageLicensing)

  const openGwsAction = useCallback(
    (gwsAction) => {
      const employee = selectedEmployees[0]
      if (!employee || !setAction || !setModal) {
        return
      }

      setAction(gwsAction)
      setModal({
        selectedEmployee: employee,
        openedModalName: MODAL_NAMES.INTEGRATION_MANAGER,
      })
    },
    [selectedEmployees, setAction, setModal],
  )

  const handleClick = useCallback(
    (action) => {
      if (isGws) {
        openGwsAction(action)
        return
      }

      openIntegrationModal?.(action)
    },
    [isGws, openGwsAction, openIntegrationModal],
  )

  const buttons = useMemo(() => {
    const definitions = isGws ? GWS_BUTTONS : M365_BUTTONS
    return definitions.filter((button) => !button.excludeWhen({ selectedEmployees }))
  }, [isGws, selectedEmployees])

  return buttons.map((button) => (
    <EmployeeActionButton
      key={button.messageId}
      action={button.action}
      icon={button.icon}
      messageId={button.messageId}
      disabled={!areActionsEnabled}
      onAction={handleClick}
    />
  ))
}

EmployeeActionsSection.propTypes = {
  isAuthorized: PropTypes.bool,
  openIntegrationModal: PropTypes.func,
  selectedEmployees: PropTypes.arrayOf(
    PropTypes.shape({
      employeeIdentifier: PropTypes.string,
      enabled: PropTypes.bool,
      isAuthorizedForEngage: PropTypes.bool,
    }),
  ).isRequired,
  overrideEngageLicensing: PropTypes.bool,
  integrationType: PropTypes.string,
  setAction: PropTypes.func,
  setModal: PropTypes.func,
}

export default EmployeeActionsSection
